import { addError } from "@wayflyer/blackbox-fe";
import type { FundingApplicationSchemas } from "api-types";
import { useRef, useState } from "react";
import { useErrorModal, useFundingApplicationMutation } from "shared-ui";

import { useFundingApplication } from "../../../queries/funding-application/queries";
import {
  getNextStepDecision,
  getPreviousDeclarationChoice,
  type AggregateCoverage,
  type BankingDataDeclarationChoice,
} from "../banking-accounts/utils";

const NOT_COMPLETED_MESSAGE = "banking-data-declaration: customer did not complete the declaration";

type PendingAnswer = { resolve: () => void; reject: (error: Error) => void };

type UseBankingDataDeclarationParams = {
  customerDomain: string;
  hasProvidedBankingData: boolean;
  coverageKind: AggregateCoverage["kind"];
  hasAccountsToReview: boolean;
  openModal: () => void;
  closeModal: () => void;
};

export const useBankingDataDeclaration = ({
  customerDomain,
  hasProvidedBankingData,
  coverageKind,
  hasAccountsToReview,
  openModal,
  closeModal,
}: UseBankingDataDeclarationParams) => {
  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);
  const { openErrorModal } = useErrorModal();
  const pendingAnswer = useRef<PendingAnswer | null>(null);
  const [hasChosenToAddMissing, setHasChosenToAddMissing] = useState(false);

  const saveDeclaration = async (bankingDataDeclaration: FundingApplicationSchemas.BankingDataDeclaration) => {
    try {
      await fundingApplicationMutation.mutateAsync({ id: fundingApplication.id, bankingDataDeclaration });
      return true;
    } catch (e: any) {
      openErrorModal({ variant: "submit-error", error: e });
      addError(e, { message: "banking-data-declaration: save error" });
      return false;
    }
  };

  const settlePendingAnswer = (outcome: "continue" | "stay") => {
    const pending = pendingAnswer.current;
    pendingAnswer.current = null;
    if (outcome === "continue") {
      pending?.resolve();
    } else {
      pending?.reject(new Error(NOT_COMPLETED_MESSAGE));
    }
  };

  // Resolves when the customer may leave the page; rejects when they should stay.
  const ensureDeclaration = async () => {
    const decision = getNextStepDecision({
      hasProvidedBankingData,
      coverageKind,
      hasAccountsToReview,
      declaration: fundingApplication.bankingDataDeclaration,
    });

    if (decision === "record-detected" && !(await saveDeclaration("full_period_detected"))) {
      throw new Error(NOT_COMPLETED_MESSAGE);
    }
    if (decision === "ask") {
      settlePendingAnswer("stay");
      await new Promise<void>((resolve, reject) => {
        pendingAnswer.current = { resolve, reject };
        openModal();
      });
    }
  };

  const onConfirm = async (choice: BankingDataDeclarationChoice) => {
    if (choice === "will_add_missing") {
      setHasChosenToAddMissing(true);
      onDismiss();
      return;
    }
    // TODO(ONB-610): on cannot_provide, backfill the account-level flag on every "to review" account.
    const answeredQuestion = pendingAnswer.current;
    if (!(await saveDeclaration(choice))) {
      return;
    }
    // The modal may have been dismissed and reopened for a new question while this was saving.
    if (pendingAnswer.current !== answeredQuestion) {
      return;
    }
    closeModal();
    settlePendingAnswer("continue");
  };

  const onDismiss = () => {
    closeModal();
    settlePendingAnswer("stay");
  };

  return {
    ensureDeclaration,
    onConfirm,
    onDismiss,
    shouldShowMissingPeriodBanner: hasChosenToAddMissing && hasProvidedBankingData && coverageKind === "gap",
    previousChoice: getPreviousDeclarationChoice(fundingApplication.bankingDataDeclaration),
    isSaving: fundingApplicationMutation.isPending,
  };
};
